import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users } from 'lucide-react';
import api from '../services/api';
import { formatProfilePictureUrl } from '../utils/profilePictureHelper';
import { showError } from '../utils/showError';
import { extractErrorMessage } from '../utils/errorUtils';
import {
  PageHeader,
  SearchBar,
  LoadingSpinner,
  ErrorMessage,
  ListContainer,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  EmptyState,
  Card,
  CardBody,
  Badge,
  Button,
  Pagination,
  ConfirmationModal
} from '../components/common';

const BuildingMembers = () => {
  const { buildingId } = useParams();
  // ... (skip lines just ensuring the top import match)
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [building, setBuilding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [removeModal, setRemoveModal] = useState({ isOpen: false, userId: null, memberName: '' });
  const [removing, setRemoving] = useState(false);
  const pageSize = 10;

  useEffect(() => {
    fetchBuilding();
    fetchMembers();
  }, [buildingId, currentPage, searchTerm]);

  const fetchBuilding = async () => {
    try {
      const response = await api.get(`/Building/${buildingId}`);
      setBuilding(response.data);
    } catch (err) {
      console.error('Error fetching building:', err);
    }
  };

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const params = {
        pageNumber: currentPage,
        pageSize: pageSize,
        ...(searchTerm && { searchString: searchTerm })
      };

      const response = await api.get(`/Building/${buildingId}/members`, { params });
      setMembers(response.data.items || []);
      setTotalPages(response.data.totalPages);
      setTotalCount(response.data.totalCount);
    } catch (err) {
      setError(extractErrorMessage(err));
      console.error('Error fetching members:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchMembers();
  };

  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (e.target.value === '') {
      setCurrentPage(1);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRemoveMember = (member) => {
    setRemoveModal({
      isOpen: true,
      userId: member.userId,
      memberName: `${member.firstName} ${member.lastName}`
    });
  };

  const confirmRemove = async () => {
    if (!removeModal.userId) return;

    setRemoving(true);
    try {
      await api.delete(`/Building/${buildingId}/members/${removeModal.userId}`);
      fetchMembers();
      setRemoveModal({ isOpen: false, userId: null, memberName: '' });
    } catch (err) {
      console.error('Error removing member:', err);
      showError(err);
    } finally {
      setRemoving(false);
    }
  };

  const getRoleBadges = (member) => {
    const badges = [];
    if (member.isAdmin) badges.push({ text: 'Admin', variant: 'danger' });
    if (member.isManager) badges.push({ text: 'Manager', variant: 'primary' });
    if (member.isOwner) badges.push({ text: 'Owner', variant: 'accent' });
    if (member.isResident) badges.push({ text: 'Resident', variant: 'info' });
    if (member.isStaff) badges.push({ text: 'Staff', variant: 'outline' });
    return badges;
  };

  if (loading && members.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Building Members - ${building?.name || 'Loading...'}`}
        subtitle="Manage building members and their roles"
        backLink={{
          label: "Back to Building Details",
          href: `/buildings/${buildingId}`
        }}
        actions={[
          <Button
            key="join-requests"
            variant="secondary"
            onClick={() => navigate(`/buildings/${buildingId}/join-requests`)}
          >
            Join Requests
          </Button>,
          <Button
            key="view-units"
            variant="accent"
            onClick={() => navigate(`/buildings/${buildingId}/units`)}
          >
            View Units
          </Button>
        ]}
      />

      {error && (
        <ErrorMessage message={error} onDismiss={() => setError(null)} />
      )}

      <Card>
        <CardBody>
          <SearchBar
            placeholder="Search members..."
            value={searchTerm}
            onChange={setSearchTerm}
            onSearch={handleSearch}
          />
        </CardBody>
      </Card>

      <ListContainer>
        {members.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No members found"
            description="No members match your search."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableCell header>Member</TableCell>
                <TableCell header>Contact</TableCell>
                <TableCell header>Roles</TableCell>
                <TableCell header>
                  <span className="sr-only">Actions</span>
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.userId}>
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <img
                        className="h-10 w-10 rounded-full object-cover border border-slate-200"
                        src={member.profilePictureUrl ? formatProfilePictureUrl(member.profilePictureUrl) : `https://ui-avatars.com/api/?name=${encodeURIComponent(member.firstName + ' ' + member.lastName)}&background=random`}
                        alt=""
                      />
                      <div>
                        <div className="text-sm font-semibold text-slate-900 group-hover:text-primary-600 transition-colors">
                          {member.firstName} {member.lastName}
                        </div>
                        <div className="text-sm text-slate-500 mt-0.5">
                          {member.email}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-slate-700">
                      {member.mobilePhone && <div>{member.mobilePhone}</div>}
                      {(member.unitNumber || member.floor) && (
                        <div className="mt-0.5 text-slate-500">
                          Unit: {member.unitNumber || 'N/A'}{member.floor ? `, Floor: ${member.floor}` : ''}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1.5">
                      {getRoleBadges(member).map((badge, index) => (
                        <Badge
                          key={index}
                          variant={badge.variant}
                          size="small"
                        >
                          {badge.text}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end pr-4">
                      <Button
                        onClick={() => handleRemoveMember(member)}
                        variant="ghost"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        size="small"
                      >
                        Remove
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ListContainer>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={handlePageChange}
        />
      )}
      <ConfirmationModal
        isOpen={removeModal.isOpen}
        onClose={() => setRemoveModal({ isOpen: false, userId: null, memberName: '' })}
        onConfirm={confirmRemove}
        title="Remove Member"
        message={`Are you sure you want to remove "${removeModal.memberName}" from this building? This action cannot be undone.`}
        confirmText="Remove"
        confirmVariant="danger"
        loading={removing}
      />
    </div>
  );
};

export default BuildingMembers;